import React, { useMemo } from 'react';
import { Platform, View, Text } from 'react-native';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import ReactApexChart from 'react-apexcharts';

interface SalesChartProps {
  salesStats: {
    labels?: string[];
    data?: number[];
    series?: Array<{ name: string; data: number[] }>;
  } | null;
  height?: number;
}

export const SalesAnalyticsChart: React.FC<SalesChartProps> = ({ salesStats, height = 270 }) => {
  const theme = themeSelector();

  const chartOptions = useMemo(() => {
    if (!ReactApexChart) return null;

    const labels = salesStats?.labels || salesStats?.series?.[0]?.data?.map((_, i) => `Period ${i + 1}`) || [];
    const seriesData = salesStats?.series || salesStats?.data ? [{ name: 'Total Sales', data: salesStats?.data || [] }] : [];

    return {
      series: seriesData,
      chart: {
        type: 'area' as const,
        height: height,
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: 'inherit',
      },
      dataLabels: { enabled: false },
      stroke: {
        curve: 'smooth' as const,
        width: 2,
        colors: ['#0ea5e9', '#10b981', '#f59e0b'],
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.3,
          stops: [0, 90, 100],
          colorStops: [
            {
              offset: 0,
              color: '#0ea5e9',
              opacity: 0.7,
            },
            {
              offset: 100,
              color: '#0ea5e9',
              opacity: 0.1,
            },
          ],
        },
      },
      colors: ['#0ea5e9', '#10b981', '#f59e0b'],
      xaxis: {
        categories: labels,
        labels: {
          style: {
            colors: theme ? '#64748b' : '#94a3b8',
            fontSize: '12px',
          },
        },
        axisBorder: {
          color: theme ? '#e2e8f0' : '#334155',
        },
        axisTicks: {
          color: theme ? '#e2e8f0' : '#334155',
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: theme ? '#64748b' : '#94a3b8',
            fontSize: '12px',
          },
          formatter: (value: number) => {
            if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
            return value.toString();
          },
        },
      },
      grid: {
        borderColor: theme ? '#e2e8f0' : '#334155',
        strokeDashArray: 4,
      },
      tooltip: {
        theme: theme ? 'light' : 'dark',
        style: {
          fontSize: '12px',
        },
      },
      legend: {
        show: true,
        position: 'top' as const,
        horizontalAlign: 'right' as const,
        labels: {
          colors: theme ? '#1e293b' : '#f1f5f9',
        },
      },
    };
  }, [salesStats, height, theme]);

  if (!ReactApexChart || !chartOptions || !salesStats) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
        <View style={{ padding: 20, borderRadius: 12, backgroundColor: theme ? '#f8fafc' : '#334155' }}>
          {Platform.OS === 'web' ? (
            <p style={{ color: theme ? '#64748b' : '#94a3b8', fontSize: 14, textAlign: 'center', margin: 0 }}>
              {!ReactApexChart ? 'Charts not available on this platform' : 'No sales data available'}
            </p>
          ) : (
            <Text style={{ color: theme ? '#64748b' : '#94a3b8', fontSize: 14, textAlign: 'center' }}>
              {!ReactApexChart ? 'Charts not available on this platform' : 'No sales data available'}
            </Text>
          )}
        </View>
      </View>
    );
  }

  return (
    <div style={{ width: '100%', height: `${height}px` }}>
      <ReactApexChart
        options={chartOptions}
        series={chartOptions.series}
        type="area"
        height={height}
      />
    </div>
  );
};

interface ApiCallsChartProps {
  chartData: {
    labels?: string[];
    data?: number[];
    series?: number[];
  };
  height?: number;
  onDateRangeChange?: (startDate: Date, endDate: Date) => void;
}

export const ApiCallsChart: React.FC<ApiCallsChartProps> = ({ chartData, height = 400, onDateRangeChange }) => {
  const theme = themeSelector();

  const chartOptions = useMemo(() => {
    if (!ReactApexChart) return null;

    const labels = chartData?.labels || [];
    const data = chartData?.data || chartData?.series || [];

    return {
      series: [
        {
          name: 'API Calls',
          data: data,
        },
      ],
      chart: {
        type: 'line' as const,
        height: height,
        toolbar: { show: true, tools: { download: true, selection: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true } },
        zoom: { enabled: true, type: 'x' as const },
        fontFamily: 'inherit',
      },
      dataLabels: { enabled: false },
      stroke: {
        curve: 'smooth' as const,
        width: 3,
        colors: ['#0ea5e9'],
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.2,
          stops: [0, 90, 100],
          colorStops: [
            {
              offset: 0,
              color: '#0ea5e9',
              opacity: 0.7,
            },
            {
              offset: 100,
              color: '#0ea5e9',
              opacity: 0.1,
            },
          ],
        },
      },
      colors: ['#0ea5e9'],
      markers: {
        size: 4,
        colors: ['#0ea5e9'],
        strokeColors: '#fff',
        strokeWidth: 2,
        hover: {
          size: 6,
        },
      },
      xaxis: {
        categories: labels,
        labels: {
          style: {
            colors: theme ? '#64748b' : '#94a3b8',
            fontSize: '12px',
          },
          rotate: -45,
          rotateAlways: false,
        },
        axisBorder: {
          color: theme ? '#e2e8f0' : '#334155',
        },
        axisTicks: {
          color: theme ? '#e2e8f0' : '#334155',
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: theme ? '#64748b' : '#94a3b8',
            fontSize: '12px',
          },
          formatter: (value: number) => {
            if (value >= 1000) return `${(value / 1000).toFixed(1)}k`;
            return value.toString();
          },
        },
      },
      grid: {
        borderColor: theme ? '#e2e8f0' : '#334155',
        strokeDashArray: 4,
      },
      tooltip: {
        theme: theme ? 'light' : 'dark',
        style: {
          fontSize: '12px',
        },
        y: {
          formatter: (value: number) => `${value} calls`,
        },
      },
    };
  }, [chartData, height, theme]);

  if (!ReactApexChart || !chartOptions || !chartData || (!chartData.labels?.length && !chartData.data?.length)) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
        <View style={{ padding: 20, borderRadius: 12, backgroundColor: theme ? '#f8fafc' : '#334155' }}>
          {Platform.OS === 'web' ? (
            <>
              <p style={{ color: theme ? '#64748b' : '#94a3b8', fontSize: 14, textAlign: 'center', margin: 0 }}>
                {!ReactApexChart ? 'Charts not available on this platform' : 'No API call data available for the selected date range'}
              </p>
              {chartOptions && (
                <p style={{ color: theme ? '#94a3b8' : '#64748b', fontSize: 12, textAlign: 'center', marginTop: 8, marginBottom: 0 }}>
                  Try selecting a different date range or check back later
                </p>
              )}
            </>
          ) : (
            <>
              <Text style={{ color: theme ? '#64748b' : '#94a3b8', fontSize: 14, textAlign: 'center' }}>
                {!ReactApexChart ? 'Charts not available on this platform' : 'No API call data available for the selected date range'}
              </Text>
              {chartOptions && (
                <Text style={{ color: theme ? '#94a3b8' : '#64748b', fontSize: 12, textAlign: 'center', marginTop: 8 }}>
                  Try selecting a different date range or check back later
                </Text>
              )}
            </>
          )}
        </View>
      </View>
    );
  }

  return (
    <div style={{ width: '100%', height: `${height}px` }}>
      <ReactApexChart
        options={chartOptions}
        series={chartOptions.series}
        type="area"
        height={height}
      />
    </div>
  );
};

interface DonutChartProps {
  data: {
    data: number[];
    labels: string[];
    colors: string[];
  };
  height?: number;
}

export const UsageDonutChart: React.FC<DonutChartProps> = ({ data, height = 300 }) => {
  const theme = themeSelector();

  const chartOptions = useMemo(() => {
    if (!ReactApexChart) return null;

    return {
      series: data.data,
      chart: {
        type: 'donut' as const,
        height: height,
        fontFamily: 'inherit',
      },
      labels: data.labels,
      colors: data.colors,
      plotOptions: {
        pie: {
          donut: {
            size: '70%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: '14px',
                fontWeight: 600,
                color: theme ? '#1e293b' : '#f1f5f9',
              },
              value: {
                show: true,
                fontSize: '16px',
                fontWeight: 700,
                color: theme ? '#1e293b' : '#f1f5f9',
                formatter: (val: string) => val,
              },
              total: {
                show: true,
                label: 'Total',
                fontSize: '14px',
                fontWeight: 600,
                color: theme ? '#64748b' : '#94a3b8',
                formatter: () => {
                  return data.data.reduce((a: number, b: number) => a + b, 0).toFixed(2);
                },
              },
            },
          },
        },
      },
      legend: {
        show: true,
        position: 'bottom' as const,
        horizontalAlign: 'center' as const,
        labels: {
          colors: theme ? '#1e293b' : '#f1f5f9',
        },
      },
      tooltip: {
        theme: theme ? 'light' : 'dark',
        style: {
          fontSize: '12px',
        },
      },
      dataLabels: {
        enabled: false,
      },
    };
  }, [data, height, theme]);

  if (!ReactApexChart || !chartOptions || !data.data.length) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
        <View style={{ padding: 20, borderRadius: 12, backgroundColor: theme ? '#f8fafc' : '#334155' }}>
          {Platform.OS === 'web' ? (
            <p style={{ color: theme ? '#64748b' : '#94a3b8', fontSize: 14, textAlign: 'center', margin: 0 }}>
              {!ReactApexChart ? 'Charts not available on this platform' : 'No usage data available'}
            </p>
          ) : (
            <Text style={{ color: theme ? '#64748b' : '#94a3b8', fontSize: 14, textAlign: 'center' }}>
              {!ReactApexChart ? 'Charts not available on this platform' : 'No usage data available'}
            </Text>
          )}
        </View>
      </View>
    );
  }

  return (
    <div style={{ width: '100%', height: `${height}px` }}>
      <ReactApexChart
        options={chartOptions}
        series={chartOptions.series}
        type="donut"
        height={height}
      />
    </div>
  );
};

